/**
 * ===========================================================================
 * 商品资产的**可选项与 CSV 契约** —— 唯一真源
 * ===========================================================================
 *
 * 用户口径（2026-10-08）：「新增一个商品资产管理的模块页面，这个是可以让客户
 * 新增，批量上传文件新增的，包括：标题，卖点，语言，图片，需要等等，因为这个可以
 * 让商品详情页可以直接引用，就不需要自己输出产品图片跟文案那些」。
 *
 * 这一层只管两件事：**表单里能选什么**（语言清单）与**表格怎么读**（CSV 契约）。
 * 它不管「商品存在哪」「详情页怎么引用」—— 那两个分别在 `api/` 与页面里。
 *
 * ### 三条约束
 *
 * 1. **语言的唯一真源在这里**。后端的 `language` 列**不校验取值**，只存字符串 ——
 *    后端再列一份语种清单，就是同一份枚举写两处，加一个语种要改两处且不会报错
 *    （正是铁律里「枚举单一真源」那条）。
 *
 * 2. **筛选条不用这份清单**。筛选条读的是 `GET /api/product-assets/facets`
 *    的**真实分布** —— 拿这份枚举去铺筛选 chip，会得到一排「点了必然为空」的档
 *    （用户从没用过德语，那颗 chip 就是死的）。**可选项**与**已有值**是两件事。
 *
 * 3. **CSV 表头按同一套归一口径**（`normKey`），底层解析复用 `config/batchCsv.ts`
 *    的实现 —— 那是全项目唯一一份处理「引号内换行 / `""` 转义」的正确实现。
 *    这里只声明「商品表有哪几列」，不重写解析器。
 */

import { detectDelimiter, normKey, parseDelimited } from './batchCsv'

/* -------------------------------------------------------------------------- */
/* 语言                                                                        */
/* -------------------------------------------------------------------------- */

export type ProductLanguage = {
  id: string
  label: string
}

/**
 * 新增表单里能选的语言。**id 会原样存进库并下发给上游**，
 * 所以它们是 BCP-47 风格的标签（`zh-CN`），不是「中文」这种展示名 ——
 * 展示名只在界面上出现，把它存进库，下一次按语言筛选就永远匹配不上。
 *
 * 覆盖的是当前主营的几个市场（北美 / 欧洲 / 俄语区 / 东南亚 / 中东 / 拉美），
 * 与 `config/platforms.ts` 的平台清单同源。**不必求全**：用户在 CSV 里写别的
 * 语种不会被拒绝（`normalizeLanguage` 认不出就原样保留），只是表单下拉里选不到。
 */
export const PRODUCT_LANGUAGES: ProductLanguage[] = [
  { id: 'zh-CN', label: '中文（简体）' },
  { id: 'zh-TW', label: '中文（繁体）' },
  { id: 'en-US', label: 'English（美国）' },
  { id: 'en-GB', label: 'English（英国）' },
  { id: 'ja-JP', label: '日本語' },
  { id: 'ko-KR', label: '한국어' },
  { id: 'ru-RU', label: 'Русский' },
  { id: 'de-DE', label: 'Deutsch' },
  { id: 'fr-FR', label: 'Français' },
  { id: 'es-ES', label: 'Español' },
  { id: 'pt-BR', label: 'Português（巴西）' },
  { id: 'it-IT', label: 'Italiano' },
  { id: 'th-TH', label: 'ไทย' },
  { id: 'vi-VN', label: 'Tiếng Việt' },
  { id: 'ar-SA', label: 'العربية' },
]

export const DEFAULT_PRODUCT_LANGUAGE = 'zh-CN'

/** 库里存的是 id，界面上要给人看 label；认不出的 id 原样显示（别显示成空白） */
export function productLanguageLabel(id: string): string {
  if (!id) return ''
  return PRODUCT_LANGUAGES.find((l) => l.id === id)?.label ?? id
}

/** 宽松别名 → 标准 id。用户会写「中文」「English」「english」 */
const LANGUAGE_ALIASES: Record<string, string> = {
  zh: 'zh-CN',
  中文: 'zh-CN',
  简体: 'zh-CN',
  简体中文: 'zh-CN',
  中文简体: 'zh-CN',
  汉语: 'zh-CN',
  chinese: 'zh-CN',
  繁体: 'zh-TW',
  繁體: 'zh-TW',
  繁体中文: 'zh-TW',
  中文繁体: 'zh-TW',
  en: 'en-US',
  英文: 'en-US',
  英语: 'en-US',
  english: 'en-US',
  eng: 'en-US',
  ja: 'ja-JP',
  日文: 'ja-JP',
  日语: 'ja-JP',
  japanese: 'ja-JP',
  ko: 'ko-KR',
  韩文: 'ko-KR',
  韩语: 'ko-KR',
  korean: 'ko-KR',
  ru: 'ru-RU',
  俄文: 'ru-RU',
  俄语: 'ru-RU',
  russian: 'ru-RU',
  de: 'de-DE',
  德文: 'de-DE',
  德语: 'de-DE',
  german: 'de-DE',
  fr: 'fr-FR',
  法文: 'fr-FR',
  法语: 'fr-FR',
  french: 'fr-FR',
  es: 'es-ES',
  西班牙语: 'es-ES',
  spanish: 'es-ES',
  pt: 'pt-BR',
  葡萄牙语: 'pt-BR',
  巴西: 'pt-BR',
  th: 'th-TH',
  泰语: 'th-TH',
  vi: 'vi-VN',
  越南语: 'vi-VN',
  ar: 'ar-SA',
  阿拉伯语: 'ar-SA',
}

/**
 * 归一语言值。**认不出就原样返回，不拒绝** ——
 * CSV 里写一个我们没列进下拉的语种（比如「希伯来语」），
 * 拒绝整批的代价远大于「筛选条上多一个没见过的值」。
 */
export function normalizeLanguage(raw: string): string {
  const clean = (raw ?? '').trim()
  if (!clean) return ''
  const hit = LANGUAGE_ALIASES[clean.toLowerCase()] ?? LANGUAGE_ALIASES[clean]
  if (hit) return hit
  // 已带地区码（`zh-CN`、`en_US`）→ 规范成连字符写法
  const m = /^([a-z]{2})[-_]([a-z]{2})$/i.exec(clean)
  if (m) return `${m[1].toLowerCase()}-${m[2].toUpperCase()}`
  // 只有两字母主语言码 → 补一个默认地区（`en` → `en-US`）
  if (/^[a-z]{2}$/i.test(clean)) return LANGUAGE_ALIASES[clean.toLowerCase()] ?? clean.toLowerCase()
  return clean
}

/* -------------------------------------------------------------------------- */
/* 图组                                                                        */
/* -------------------------------------------------------------------------- */

/**
 * 一个商品最多几张图。**与后端 `routers/product_assets.py` 的 `MAX_IMAGES` 对齐**。
 *
 * 跨语言的两个数不能共享常量，所以这里只能各写一份 —— 但**两个地方的值必须相同**：
 * 前端放宽而后端收紧，症状是「用户传了 25 张、提交后只剩 30 张里的前 30 张」
 * 这种看不出错在哪的静默截断。改这个数时，后端那处一起改。
 */
export const PRODUCT_IMAGES_MAX = 30

/* -------------------------------------------------------------------------- */
/* 每页条数                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * 商品资产页每页几个。**与后端 `perPage` 上限（500）无关** ——
 * 那是「一次最多能要多少」，这是「一屏放几个」。
 *
 * 24 的取法：桌面网格是 `repeat(auto-fill, minmax(200px, 1fr))`，
 * 宽屏一行 5-6 张 ⇒ 24 正好 4 行出头，一屏能扫完且不触发长滚动。
 */
export const PRODUCT_PAGE_SIZE = 24

/**
 * 「从商品库选择」弹层的每页个数。弹层比整页窄一点（980px），
 * 一行 4-5 张，**同样取 24** —— 两个页面用同一个数，用户切换时不至于
 * 「刚在商品库看到第 4 行第 2 个，弹层里它跑到了第 3 行」。
 */
export const PRODUCT_PICKER_PAGE_SIZE = 24

/* -------------------------------------------------------------------------- */
/* CSV 契约                                                                    */
/* -------------------------------------------------------------------------- */

/** 一行的商品信息。**没有 `line`** —— 行号由解析器附加，不由表格提供 */
export type ProductAssetRowCore = {
  title: string
  sellingPoints: string
  language: string
  sku: string
  platform: string
  category: string
}

export type ProductAssetColumn = {
  key: keyof ProductAssetRowCore | 'imageNames' | 'images'
  label: string
  required: boolean
  hint: string
}

/**
 * 表格列 —— **唯一真源**（模板 CSV 与解析器的表头白名单都从这里生成）。
 *
 * 与批量出图的表（`batchCsv.BATCH_COLUMNS`）的差别只有一处，但那处很关键：
 * 那边是「一个产品 + 一批出图参数」，所以 `platform` 这类**参数列必须拒收**
 * （全批次共用一份，按行覆盖就是同一个字段两个入口）；
 * 这里是「一行一个商品」，平台是**这个商品自己的属性** —— 逐行不同才是常态，
 * 所以它在这里是合法列。两条规则不冲突，因为管的不是同一件事。
 */
export const PRODUCT_ASSET_COLUMNS: ProductAssetColumn[] = [
  { key: 'title', label: '标题', required: true, hint: '必填。任何一行留空，整批都导不进去' },
  {
    key: 'sellingPoints',
    label: '卖点',
    required: false,
    hint: '一行一个卖点。单元格里按回车换行（记得给这一格加英文引号），或写 | 分隔',
  },
  { key: 'language', label: '语言', required: false, hint: '如 zh-CN / en-US，留空按 zh-CN' },
  { key: 'sku', label: 'SKU', required: false, hint: '货号。图片文件名按它自动归位' },
  { key: 'platform', label: '平台', required: false, hint: '如 amazon / shopify，留空表示不限定' },
  {
    key: 'category',
    label: '分类',
    required: false,
    hint: '如 家居日用 / 服饰箱包。写一个还不存在的分类名，导入时会自动把它建出来',
  },
  {
    key: 'imageNames',
    label: '图片文件名',
    required: false,
    hint: '这个商品的图叫什么（多个用 | 分隔）。留空则按文件名自动归位',
  },
  {
    key: 'images',
    label: '图片地址',
    required: false,
    hint: '公网图地址（多个用 | 分隔）。手上已有链接时用，与本地图片二选一',
  },
]

/** 列名宽容表 —— 用户写中文、写驼峰、写带空格的，都认 */
const HEADER_ALIASES: Record<string, ProductAssetColumn['key']> = {
  title: 'title',
  标题: 'title',
  商品标题: 'title',
  产品标题: 'title',
  产品名称: 'title',
  商品名称: 'title',
  品名: 'title',
  sellingpoints: 'sellingPoints',
  卖点: 'sellingPoints',
  卖点文案: 'sellingPoints',
  卖点文案要点: 'sellingPoints',
  文案要点: 'sellingPoints',
  卖点要点: 'sellingPoints',
  商品卖点: 'sellingPoints',
  language: 'language',
  语言: 'language',
  语种: 'language',
  文案语言: 'language',
  sku: 'sku',
  编号: 'sku',
  货号: 'sku',
  款号: 'sku',
  商品编码: 'sku',
  platform: 'platform',
  平台: 'platform',
  投放平台: 'platform',
  销售平台: 'platform',
  category: 'category',
  分类: 'category',
  类别: 'category',
  类目: 'category',
  商品分类: 'category',
  商品类别: 'category',
  品类: 'category',
  imagenames: 'imageNames',
  图片文件名: 'imageNames',
  文件名: 'imageNames',
  图片名: 'imageNames',
  images: 'images',
  图片: 'images',
  图片地址: 'images',
  图片链接: 'images',
  图地址: 'images',
}

export type ParsedProductAssetRow = ProductAssetRowCore & {
  /** **CSV 里的行号**（表头是第 1 行，数据从第 2 行起）—— 报错与图片归位都按它 */
  line: number
  /** CSV 点名的图片文件名（可能为空 → 走文件名自动归位） */
  imageFileNames: string[]
  /** CSV 给的公网图地址 */
  imageUrls: string[]
}

export type ProductAssetCsvResult = {
  rows: ParsedProductAssetRow[]
  /** 表头都认不出来时的致命错误。非空时 `rows` 为空 —— 这时候继续导入毫无意义 */
  fatal: string
  /** 认不出、已忽略的列名（说一声，别默默吞掉） */
  ignoredHeaders: string[]
  /** 逐行提示（非致命） */
  warnings: string[]
}

/** 多值单元格的分隔：`|` 与换行都认（逗号不认 —— 产品名里常带逗号） */
function splitMulti(v: string): string[] {
  return (v ?? '')
    .split(/[|\n]/)
    .map((s) => s.trim())
    .filter(Boolean)
}

/**
 * 解析商品资产的 CSV。
 *
 * 三条与批量出图那边一致的原则：
 *   · **认不出就报**，不猜。表头一个都没命中 → `fatal`，而不是导出一批空商品。
 *   · **不执行任何东西**。纯字符串扫描（见 batchCsv.ts 的约束 3）。
 *   · **逐行提示可带，但不静默改数**。标题为空的行在页面上标红拦住，
 *     由用户自己决定删行还是补上（后端那边是整批 400）。
 */
export function parseProductAssetCsv(text: string): ProductAssetCsvResult {
  const raw = (text ?? '').replace(/^\uFEFF/, '')
  if (!raw.trim()) {
    return { rows: [], fatal: '表格是空的', ignoredHeaders: [], warnings: [] }
  }

  const table = parseDelimited(raw, detectDelimiter(raw.split(/\r?\n/, 1)[0] ?? ''))
  const headerRow = table[0] ?? []
  const map = new Map<number, ProductAssetColumn['key']>()
  const ignoredHeaders: string[] = []

  headerRow.forEach((cell, i) => {
    const key = HEADER_ALIASES[normKey(cell)]
    if (key) {
      // 同一列写两次时**以后一处为准**：用户多半是把前一处写错了，
      // 而 Excel 里两列同名肉眼极难发现，报错反而问不到点子上。
      map.set(i, key)
    } else if (cell.trim()) {
      ignoredHeaders.push(cell.trim())
    }
  })

  if (![...map.values()].includes('title')) {
    const seen = map.size ? `已认出的列：${[...map.values()].join('、')}` : '一列都没认出来'
    return {
      rows: [],
      fatal: `表格里没有「标题」列 —— ${seen}。第一行必须是表头（标题 / 卖点 / 语言 / SKU / 平台 / 图片文件名）`,
      ignoredHeaders,
      warnings: [],
    }
  }

  const rows: ParsedProductAssetRow[] = []
  const warnings: string[] = []

  for (let r = 1; r < table.length; r += 1) {
    const cells = table[r]
    // 整行空白直接跳过（Excel 存 CSV 常在末尾留一行空行）
    if (!cells.some((c) => c.trim())) continue

    const got: Partial<Record<ProductAssetColumn['key'], string>> = {}
    map.forEach((key, i) => {
      got[key] = (cells[i] ?? '').trim()
    })

    const line = r + 1
    const title = (got.title ?? '').trim()
    if (!title) warnings.push(`第 ${line} 行没有标题，导入前请补上或删掉这一行`)
    /**
     * 模板自带的示例行必须能被认出来（与 `batchCsv.validateRows` 同一条约定）。
     *
     * 这里不比 `/batch` 那边严重 —— 导进来只是多两条商品记录，删掉就行，
     * 不像出图那样真花钱。但**依然要拦一句**，因为模板里那两行示例用的是
     * 真实商品名：用户「下载模板 → 把自己的数据接在后面 → 直接上传」之后，
     * 库里会凭空多出两个跟他自己的货同名的商品，而他完全不知道它们从哪来。
     *
     * 判据同样挂在**用户改得动的那个字段**上（标题前缀），不靠行号或 SKU ——
     * 那两样用户一改就对不上了。
     */
    if (/^示例/.test(title)) {
      warnings.push(`第 ${line} 行像是模板里的示例行 —— 它是示例不是你的商品，导入会真的建出来`)
    }

    const language = normalizeLanguage(got.language ?? '') || DEFAULT_PRODUCT_LANGUAGE
    rows.push({
      line,
      title,
      sku: (got.sku ?? '').trim(),
      /**
       * 卖点：`|` 与换行都当分隔，最后统一成**换行连接的文本**。
       *
       * 抄的是 `batchCsv.parseBatchCsv` 对同一列的处理 —— 两边的表头都叫「卖点」，
       * 用户在 `/batch` 学会「多个用 | 分隔」，到这里写同一个写法却得到一整行
       * `0.5s速吸|可折叠`，那是把一处不一致变成了静默的脏数据。
       * 逗号不认（产品文案里常带逗号），与 `splitMulti` 一致。
       */
      sellingPoints: splitMulti(got.sellingPoints ?? '').join('\n'),
      language,
      platform: (got.platform ?? '').trim(),
      category: (got.category ?? '').trim(),
      imageFileNames: splitMulti(got.imageNames ?? ''),
      imageUrls: splitMulti(got.images ?? ''),
    })
  }

  if (!rows.length) {
    return { rows: [], fatal: '表格里只有表头，没有数据行', ignoredHeaders, warnings }
  }
  return { rows, fatal: '', ignoredHeaders, warnings }
}

/**
 * 下载用的模板 CSV（列顺序与 `PRODUCT_ASSET_COLUMNS` 一致，不另写一份）。
 *
 * 两行示例的**标题都以「示例」开头**，解析器会把它们标成示例行（见上）。
 * 用户「下载模板 → 直接上传」时就不会静默建出两个假商品 ——
 * 那两行是照着真实商品写的，不标出来在库里和真货长得一模一样。
 */
export function productAssetCsvTemplate(): string {
  const header = PRODUCT_ASSET_COLUMNS.map((c) => c.label).join(',')
  const sample = [
    '示例：硅藻土沥水垫',
    '"0.5s 速吸\n可折叠收纳\n耐高温 200℃"',
    'zh-CN',
    'HYS-001',
    'amazon',
    '家居日用',
    'HYS-001_1.jpg|HYS-001_2.jpg',
    '',
  ].join(',')
  const sample2 = [
    '示例：Cotton Contrast Skirt',
    '"Soft cotton blend\nElastic waist"',
    'en-US',
    'CS-002',
    'shopify',
    '服饰箱包',
    'CS-002_1.jpg',
    '',
  ].join(',')
  return `${header}\n${sample}\n${sample2}\n`
}

/* -------------------------------------------------------------------------- */
/* 内置示例（「导入的案例」）                                                  */
/* -------------------------------------------------------------------------- */

/**
 * 一份**可以直接导入的样例数据** —— 3 个商品，覆盖三种常见写法。
 *
 * 用户 2026-10-09 问的「导入的案例有了吗」，问的就是这个：
 * `productAssetCsvTemplate()` 是**空表 + 两行占位**，它回答「表头写什么」；
 * 而用户真正卡住的地方是「填满了该长什么样」——
 * 卖点要几行、多值怎么分隔、图片文件名与 SKU 怎么对上。
 * 光给模板，这三件事他只能猜；猜错的表现是「导进来 3 行、卖点挤成一整行」。
 *
 * ### 三条口径
 *
 * 1. **三行都用真实感数据**（不说「XXX」），因为它同时是「点击导入后看到什么」
 *    的预期 —— 假数据演示不出「中文卖点 + 英文卖点 + 一行卖点」这三种形态。
 * 2. **标题以「样例：」开头**，不是「示例：」：解析器把 `^示例` 的行标成
 *    「模板里的示例行」并给一条红色警告（那条警告是拦「下载模板后直接上传」的）。
 *    这里的样例**本来就是给用户试着导入的**，标成警告会让「填一份示例 → 点导入」
 *    这条路径看起来像做错了事。而「样例」两个字留在标题里，
 *    导入之后一眼认得出、删得掉。
 * 3. **`图片地址` 列给真实的公网图**：这样导入完卡片上就有图，
 *    「商品库长什么样」一次看全。给一个不存在的文件名只会让人以为导入坏了。
 *    （图是项目里已有的示例图床地址，与商品数据无关。）
 */
export const PRODUCT_ASSET_SAMPLE_ROWS: string[][] = [
  [
    '样例：硅藻土沥水垫',
    '"0.5s 速吸，不留水痕\n可折叠收纳不占地\n耐高温 200℃"',
    'zh-CN',
    'HYS-001',
    'amazon',
    '家居日用',
    'HYS-001_1.jpg|HYS-001_2.jpg|HYS-001_3.jpg',
    'https://i.ibb.co/FLSqr2TH/da0ae11b.png',
  ],
  [
    '样例：Cotton Contrast Skirt',
    '"Soft cotton blend, breathable\nElastic waist, true to size"',
    'en-US',
    'CS-002',
    'shopify',
    '服饰箱包',
    'CS-002_1.jpg|CS-002_2.jpg',
    '',
  ],
  [
    '样例：便携折叠水桶',
    '10L 大容量',
    'zh-CN',
    'PD-003',
    'taobao',
    '户外装备',
    'PD-003_1.jpg',
    '',
  ],
]

/**
 * 内置示例的 CSV 文本（列顺序与 `PRODUCT_ASSET_COLUMNS` 一致，不另写表头）。
 *
 * 单元格里的换行**必须包在英文引号里**（`"a\nb"`）—— 这是 CSV 的规矩，
 * 也是用户在 Excel 里让一个格子存多行卖点的唯一办法。这里现写一遍完整的
 * 引号处理，正是为了让用户看到「应该长这样」。
 */
export function productAssetCsvSample(): string {
  const header = PRODUCT_ASSET_COLUMNS.map((c) => c.label).join(',')
  const lines = PRODUCT_ASSET_SAMPLE_ROWS.map((row) => row.join(','))
  return `${header}\n${lines.join('\n')}\n`
}
